/**
 * Lazy-loaded patient workflow status history from
 * `GET /eligibility/{trial}/{mrn}/status-history`.
 */

import { useEffect, useState } from "react";
import { clinicalTrialsService } from "../../../api/clinicalTrialsService";
import type { NoteAssigneeResponse } from "../../../api/clinicalTrialsApiTypes";
import NoData from "../../../components/noData/noData";
import type { PatientStatusHistoryEntry } from "../../../data-model/eligibilityDetail";
import { useEligibilityStatusHistory } from "../../../hooks/useEligibilityHistories";
import {
  formatStatusHistoryActor,
  formatStatusHistoryTimestamp,
} from "./statusHistoryFormatting";
import "./StatusHistoryPanel.scss";

/** Props for the status history list. */
export interface StatusHistoryPanelProps {
  trialId: string;
  mrn: string;
  /** When false, no request is made. */
  active: boolean;
  /** Already-loaded facility directory; omitted when the panel should load it. */
  assignees?: NoteAssigneeResponse[];
}

/**
 * Render one workflow status transition on the vertical timeline.
 *
 * @param props - History entry and whether it is the last row
 * @returns Timeline row
 */
function StatusHistoryTimelineItem({
  entry,
  isLast,
  assignees,
}: {
  entry: PatientStatusHistoryEntry;
  isLast: boolean;
  assignees: NoteAssigneeResponse[];
}) {
  const actorLabel = formatStatusHistoryActor(entry.changed_by, assignees);

  return (
    <article className="status-history-timeline__item">
      <div className="status-history-timeline__marker" aria-hidden>
        <span className="status-history-timeline__dot" />
        {!isLast ? <span className="status-history-timeline__line" /> : null}
      </div>
      <div className="status-history-timeline__content">
        <div className="status-history-timeline__meta">
          <time dateTime={entry.changed_at}>
            {formatStatusHistoryTimestamp(entry.changed_at)}
          </time>
          <span
            className="status-history-timeline__actor"
            title={entry.changed_by}
          >
            {actorLabel}
          </span>
        </div>
        <p className="status-history-timeline__title">
          Patient Moved to <strong>{entry.patient_status}</strong>
        </p>
        {entry.comment ? (
          <div className="status-history-timeline__comment">{entry.comment}</div>
        ) : null}
      </div>
    </article>
  );
}

/**
 * Render who changed patient workflow status and when.
 *
 * @param props - Trial, MRN, and whether the parent drawer/tab is open
 * @returns Status history timeline or loading/error states
 */
export function StatusHistoryPanel({
  trialId,
  mrn,
  active,
  assignees,
}: StatusHistoryPanelProps) {
  const state = useEligibilityStatusHistory(trialId, mrn, active);
  const [loadedAssignees, setLoadedAssignees] = useState<
    NoteAssigneeResponse[]
  >([]);

  useEffect(() => {
    if (!active || assignees !== undefined) return undefined;
    const controller = new AbortController();
    clinicalTrialsService
      .getNoteAssignees(controller.signal)
      .then((members) => {
        if (!controller.signal.aborted) setLoadedAssignees(members);
      })
      .catch(() => {
        // Actor ids still render through the existing readable fallback.
      });
    return () => controller.abort();
  }, [active, assignees]);

  const actorDirectory = assignees ?? loadedAssignees;

  if (!active) {
    return null;
  }
  if (state.loading) {
    return (
      <p className="p-4 text-center text-x-tiny text-primaryGray-3">
        Loading status history…
      </p>
    );
  }
  if (state.error) {
    return (
      <div className="p-4">
        <NoData text={state.error} />
      </div>
    );
  }
  if (!state.data?.items.length) {
    return (
      <p className="p-4 text-center text-x-tiny text-primaryGray-3">
        No workflow status history yet.
      </p>
    );
  }

  const items = state.data.items;

  return (
    <div className="status-history-timeline">
      {items.map((entry, index) => (
        <StatusHistoryTimelineItem
          key={entry.history_id}
          entry={entry}
          isLast={index === items.length - 1}
          assignees={actorDirectory}
        />
      ))}
    </div>
  );
}

export default StatusHistoryPanel;
